import { TaskDoc, TaskRelationship } from "common";

export class CycleException extends Error {
  constructor(task: TaskDoc) {
    super(`Cycle detected in task dependencies: ${task.name || task._id}`);
    this.name = "CycleException";
  }
}

export class DepthFirstGraphSort {
  private m_projectTasks: TaskDoc[];
  private m_includeTask: (task: TaskDoc) => boolean;
  private m_tasks: TaskDoc[] = [];
  private m_temporaryMark: Set<TaskDoc> = new Set();
  private m_permanentMark: Set<TaskDoc> = new Set();
  private m_taskById: Map<number, TaskDoc> = new Map();

  public constructor(tasks: TaskDoc[], filter: (task: TaskDoc) => boolean) {
    this.m_projectTasks = tasks;
    this.m_taskById = new Map(this.m_projectTasks.map(t => [t.unique_id, t]));
    this.m_includeTask = filter;
  }

  public sort(): TaskDoc[] {
    try {
      for (const task of this.m_projectTasks) {
        if (this.m_includeTask(task)) {
          this.visit(task);
        }
      }

      return [...this.m_tasks].reverse();
    } finally {
      this.m_tasks = [];
      this.m_temporaryMark.clear();
      this.m_permanentMark.clear();
    }
  }

  /**
   * Retrieve the successors for the given task.
   * Note this method is intended to be overridden to augment the original functionality.
   *
   * @param task task from which to generate list of successors
   * @return list of successor relationships
   */
  public getSuccessors(task: TaskDoc): TaskRelationship[] {
    return task.successors || [];
  }

  /**
   * Get the actual TaskDoc for a successor relationship.
   * 
   * @param relation the relationship containing the successor task unique ID
   * @return the TaskDoc for the successor, or null if not found
   */
  private getSuccessorTask(relation: TaskRelationship): TaskDoc | null {
    const id = relation.successor_task_unique_id;
    if (id === undefined) {
      return null;
    }
    return this.m_taskById.get(id) || null;
  }

  /**
   * Recursively build the sorted list of tasks.
   *
   * @param task current task
   */
  private visit(task: TaskDoc): void {
    if (this.m_permanentMark.has(task)) {
      return;
    }

    if (this.m_temporaryMark.has(task)) {
      // TODO: Can we break a cycle by ignoring relation where cycle detected?
      throw new CycleException(task);
    }

    this.m_temporaryMark.add(task);

    for (const relation of this.getSuccessors(task)) {
      const successorTask = this.getSuccessorTask(relation);
      if (successorTask && this.m_includeTask(successorTask)) {
        this.visit(successorTask);
      }
    }

    this.m_temporaryMark.delete(task);
    this.m_permanentMark.add(task);
    this.m_tasks.push(task);
  }
}
